import { useEffect, useState } from 'react';
import { ArrowCounterClockwise, MagnifyingGlass } from '@/core/ui/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, FilterMenu, Page, PageHeader, type FilterOption } from '@/core/ui';
import { auditKeys, useActionCatalog } from '../application/queries';
import { periodStart, PERIODS, type Category, type Period } from '../domain/activity';
import { ActivityTimeline } from './ActivityTimeline';

export interface ActivityPageFilters {
  category?: Category;
  period: Period;
  q?: string;
}

interface Props {
  filters: ActivityPageFilters;
  onChange: (next: ActivityPageFilters) => void;
}

/**
 * O que as pessoas fizeram no portal, de todo mundo: entradas, páginas,
 * aulas, eventos, produtos e a Centuria. Filtros vão na URL — voltar da
 * página de uma pessoa devolve a mesma lista.
 */
export function ActivityPage({ filters, onChange }: Props) {
  const catalog = useActionCatalog();
  const qc = useQueryClient();
  const [search, setSearch] = useState(filters.q ?? '');

  // A busca vai para a URL quando a pessoa para de digitar.
  useEffect(() => {
    const term = search.trim() || undefined;
    if (term === filters.q) return;
    const t = window.setTimeout(() => onChange({ ...filters, q: term }), 350);
    return () => window.clearTimeout(t);
  }, [search, filters, onChange]);

  const categories: FilterOption<Category | 'todas'>[] = [
    { value: 'todas', label: 'Todas' },
    ...(catalog.data?.categories ?? []).map((c) => ({ value: c.key as Category, label: c.label })),
  ];

  return (
    <Page>
      <PageHeader
        title="Atividade"
        description="O que cada pessoa fez no portal — páginas, aulas, eventos, produtos e a Centuria. Mostra o que foi feito, nunca o que foi escrito."
        actions={
          <Button variant="secondary" onClick={() => void qc.invalidateQueries({ queryKey: auditKeys.all })}>
            <ArrowCounterClockwise aria-hidden className="size-4" />
            Atualizar
          </Button>
        }
      />

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative sm:w-80">
          <MagnifyingGlass
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-gray-400"
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por aula, curso, produto ou evento"
            aria-label="Buscar na atividade"
            className="w-full rounded-lg border border-line-strong bg-surface py-2 pr-3 pl-9 text-sm focus:ring-2 focus:ring-teal-300 focus:outline-none"
          />
        </div>
        <FilterMenu
          label="Categoria"
          value={filters.category ?? 'todas'}
          options={categories}
          onChange={(v) => onChange({ ...filters, category: v === 'todas' ? undefined : v })}
        />
        <FilterMenu
          label="Período"
          value={filters.period}
          options={PERIODS}
          onChange={(period) => onChange({ ...filters, period })}
        />
      </div>

      <ActivityTimeline
        showPerson
        filters={{ category: filters.category, from: periodStart(filters.period), q: filters.q }}
        emptyDescription={
          filters.q || filters.category || filters.period !== 'tudo'
            ? 'Nada com esses filtros. Tente ampliar o período ou limpar a busca.'
            : 'A atividade aparece aqui assim que as pessoas usarem o portal.'
        }
      />
    </Page>
  );
}
